/**
 * What the UC1 map paints for the active constraint chip.
 *
 * Pure, and in its own module rather than inside `Uc1.tsx`, for two reasons: the component
 * file stays components-only (React Fast Refresh), and "what colour is this watershed for
 * this chip" is the one rule in the whole pane worth testing on its own.
 */
import type { IntelUnitRow } from '../../lib/api';
import {
  CATEGORY_COLORS,
  CONSTRAINT_COLORS,
  NEUTRAL_COLOR,
  constraintSteps,
  shareStep,
} from './uc1Constants';

/** What one chip's pass over the units rows produces for the result layer and the legend. */
export interface ChipPaint {
  colors: Record<string, string>;
  patterns: Record<string, 'hatched'>;
  details: Record<string, string>;
  reasons: string[];
  /** NOTES #729: the legend rows — one per class actually painted, with its count. */
  classes: { label: string; color: string; hatched?: boolean; n: number }[];
}

/** A country's roll-up answer for the active constraint, as `Uc1.tsx` reads it off details. */
export interface CountryShare {
  share: number | null;
  reason: string | null;
}

/**
 * One chip's colours, hatches and tooltips over the units rows.
 *
 * An unknown dimension id (a units payload cached from before the launch definition) has no
 * `flags` entry, so every unit hatches rather than the pane throwing — a stale cache reads
 * as "not assessed", which is true of it.
 */
export function chipPaint({
  rows,
  chip,
  level,
  shareByUnit,
  label,
}: {
  rows: IntelUnitRow[];
  chip: string;
  level: string;
  shareByUnit: Map<string, CountryShare>;
  label: (key: string, options?: Record<string, unknown>) => string;
}): ChipPaint {
  const colors: Record<string, string> = {};
  const patterns: Record<string, 'hatched'> = {};
  const details: Record<string, string> = {};
  const reasons = new Set<string>();

  const hatch = (id: string, reason: string | null) => {
    patterns[id] = 'hatched';
    if (reason) {
      reasons.add(reason);
      details[id] = label('intel.uc1.legend.notAssessed', {
        reason: label(`intel.uc1.reason.${reason}`, { defaultValue: reason }),
      });
    } else {
      details[id] = label('intel.uc1.row.status.notAssessed');
    }
  };

  for (const row of rows) {
    if (chip === 'priority') {
      colors[row.unit_id] = CATEGORY_COLORS[row.category] ?? NEUTRAL_COLOR;
      details[row.unit_id] = label(`intel.uc1.category.${row.category}`, {
        defaultValue: row.category,
      });
      continue;
    }

    if (level === 'admin0') {
      const entry = shareByUnit.get(row.unit_id);
      if (!entry || entry.share == null) {
        hatch(row.unit_id, entry?.reason ?? null);
        continue;
      }
      const step = shareStep(entry.share);
      colors[row.unit_id] = constraintSteps(chip)[step];
      details[row.unit_id] = label(`intel.uc1.legend.share${['Low', 'Mid', 'High'][step]}`);
      continue;
    }

    const flag = row.flags?.[chip];
    if (flag == null) {
      hatch(row.unit_id, row.reasons?.[chip] ?? null);
      continue;
    }
    colors[row.unit_id] = flag ? (CONSTRAINT_COLORS[chip] ?? NEUTRAL_COLOR) : NEUTRAL_COLOR;
    details[row.unit_id] = label(flag ? 'intel.uc1.legend.flagged' : 'intel.uc1.legend.notFlagged');
  }

  // Legend rows, in the order the map's own palette reads, counting what was painted.
  const legend: ChipPaint['classes'] = [];
  const add = (key: string, color: string, test: (id: string) => boolean, hatched = false) => {
    const n = rows.filter((r) => test(r.unit_id)).length;
    if (n) legend.push({ label: key, color, n, ...(hatched ? { hatched: true } : {}) });
  };
  if (chip === 'priority') {
    for (const cat of ['high', 'moderate', 'low']) {
      const color = CATEGORY_COLORS[cat] ?? NEUTRAL_COLOR;
      add(label(`intel.uc1.category.${cat}`), color,
        (id) => rows.find((r) => r.unit_id === id)?.category === cat);
    }
  } else if (level === 'admin0') {
    const steps = constraintSteps(chip);
    (['Low', 'Mid', 'High'] as const).forEach((name, i) => {
      const color = steps[i] ?? NEUTRAL_COLOR;
      add(label(`intel.uc1.legend.share${name}`), color, (id) => colors[id] === color);
    });
  } else {
    add(label('intel.uc1.legend.flagged'), CONSTRAINT_COLORS[chip] ?? NEUTRAL_COLOR,
      (id) => colors[id] === (CONSTRAINT_COLORS[chip] ?? NEUTRAL_COLOR));
    add(label('intel.uc1.legend.notFlagged'), NEUTRAL_COLOR, (id) => colors[id] === NEUTRAL_COLOR);
  }
  for (const reason of reasons) {
    add(label('intel.uc1.legend.notAssessed', {
      reason: label(`intel.uc1.reason.${reason}`, { defaultValue: reason }),
    }), '#f4f6f3', (id) => patterns[id] === 'hatched' && (details[id] ?? '').includes(label(`intel.uc1.reason.${reason}`, { defaultValue: reason })), true);
  }
  if (reasons.size === 0) add(label('intel.uc1.row.status.notAssessed'), '#f4f6f3', (id) => patterns[id] === 'hatched', true);

  return { colors, patterns, details, reasons: [...reasons], classes: legend };
}
